import clsx from "clsx";
import { useState, useMemo, useContext } from "react";
import {
  deleteDatabase,
  updateDatabase,
  addDatabase,
} from "pages/dashboard/database/service";
import { SvgRemove, SvgEdit, SvgAdd } from "ui/svgs";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { showToast } from "ui/components/common";
import { Modal, Skeleton, Form, Input, Button, Empty } from "antd";
import Search from "ui/search";
import { DatabaseContext } from "context/DatabaseContext";
const { TextArea } = Input;

export default function DatabaseList() {
  const { databaseid } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { isLoading, databases, setDatabases, queryDatabases } =
    useContext(DatabaseContext);

  const [updateSubmitting, setUpdateSubmitting] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [isUpdateDatabaseModalOpen, setIsUpdateDatabaseModalOpen] =
    useState(false);
  const [isDeleteDatabaseModalOpen, setIsDeleteDatabaseModalOpen] =
    useState(false);

  const [isCreateDatabaseModalOpen, setIsCreateDatabaseModalOpen] =
    useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createForm] = Form.useForm();
  const [updateForm] = Form.useForm();

  const onCreate = async ({ name, description }) => {
    setCreateSubmitting(true);
    const res = await addDatabase(name, description);
    setCreateSubmitting(false);
    if (res.status !== 200) {
      showToast(
        1,
        "There was a problem creating the database. Please try again.",
      );
      return;
    }
    setIsCreateDatabaseModalOpen(false);
    showToast(0, "Form submitted successfully!");

    queryDatabases();
  };

  const onUpdate = async ({ name, description }) => {
    setUpdateSubmitting(true);
    const res = await updateDatabase({
      id: curDatabase?.id,
      name,
      description,
    });
    setUpdateSubmitting(false);
    if (res.status !== 200) {
      showToast(
        1,
        "There was a problem updating the database. Please try again.",
      );
      return;
    }
    const cur = databases?.find((e) => e.id === databaseid);
    cur && Object.assign(cur, { name, description });
    setDatabases([...databases]);
    setIsUpdateDatabaseModalOpen(false);
  };

  const curDatabase = useMemo(() => {
    return databases?.find((e) => e.id === databaseid);
  }, [databaseid, databases]);

  const handleDeleteDatabase = async () => {
    setDeleteSubmitting(true);
    const res = await deleteDatabase(curDatabase?.id || "");
    setDeleteSubmitting(false);
    if (res.status !== 200) {
      showToast(
        1,
        "There was a problem deleting the database. Please try again.",
      );
      return;
    }
    setIsDeleteDatabaseModalOpen(false);
    queryDatabases();
    navigate("/dashboard/database", { replace: true });
  };

  return (
    <div className="w-full flex-none md:w-96 border-r border-neutral-200 px-6 py-4 overflow-y-auto h-full">
      <div className="flex items-center justify-between gap-2 mt-4">
        <Search
          placeholder="Search database"
          className="relative flex flex-1 flex-shrink-0 h-[46px]"
          numberAndLetter={true}
        />
        <button
          className={clsx(
            "flex justify-center h-[46px] w-[46px] items-center rounded-2xl bg-[#3E83F6] text-base font-medium text-white  hover:bg-blue-400",
          )}
          onClick={() => setIsCreateDatabaseModalOpen(true)}
        >
          <SvgAdd className="fill-white " width="16" />
        </button>
      </div>
      {isLoading ? (
        <div className=" pl-4 pt-8">
          <Skeleton />
        </div>
      ) : databases?.length === 0 ? (
        <div
          className="flex flex-col justify-center h-[calc(100% - 90px)]"
          style={{ height: "calc(100% - 90px)" }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Try to create one and get started."
          />
        </div>
      ) : (
        <div className="w-full pt-4 " id="scrollableDiv">
          {databases?.map((e) => (
            <button
              key={e.id}
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                navigate(`/dashboard/database/${e.id}?${params.toString()}`, {
                  replace: true,
                });
              }}
              className={clsx(
                "w-full gap-x-2 p-6 hover:bg-[#DCE7FD] rounded-3xl mb-4 text-neutral-800 group",
                databaseid === e.id ? "bg-[#DCE7FD]" : "bg-[#F5F5F5]",
              )}
            >
              <div className="flex justify-between items-center">
                <p className="text-left font-semibold max-w-[200] overflow-hidden break-words text-ellipsis text-nowrap">
                  {e.name}
                </p>
                <div className="invisible group-hover:visible flex-shrink-0">
                  <button
                    className="rounded-full text-neutral-500 p-1 fill-neutral-500 hover:fill-neutral-400"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsUpdateDatabaseModalOpen(true);
                    }}
                  >
                    <SvgEdit width="18" />
                  </button>
                  <button
                    className="rounded-full text-neutral-500 p-1 fill-neutral-500 hover:fill-neutral-400"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsDeleteDatabaseModalOpen(true);
                    }}
                  >
                    <SvgRemove width="19" />
                  </button>
                </div>
              </div>
              <p className=" text-[#717171] max-w-[200] overflow-hidden break-words text-ellipsis text-nowrap text-left ">
                {e.description}
              </p>
              <p className="mt-1 text-[#222222] text-left ">
                <span className="text-blue-500 font-semibold mr-1">
                  {e.recordCount ?? "-"}
                </span>
                Records
              </p>
            </button>
          ))}
          <div
            className="text-sm text-center text-neutral-500 h-1"
            id="needLoadMore"
          />

          <Modal
            title={"Update Database"}
            open={isUpdateDatabaseModalOpen}
            closable={false}
            wrapClassName="database-modal"
            centered
            footer={null}
            width={600}
            destroyOnClose={true}
          >
            <Form
              form={updateForm}
              name="basic"
              layout="vertical"
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 16 }}
              onFinish={onUpdate}
              className="pt-4"
              autoComplete="off"
              initialValues={{
                name: curDatabase?.name,
                description: curDatabase?.description,
              }}
              clearOnDestroy
            >
              <Form.Item
                label="Name"
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Please input the database name!",
                  },
                ]}
              >
                <Input
                  className="w-[550px] h-[54px] rounded-xl"
                  placeholder="Enter Name"
                />
              </Form.Item>

              <Form.Item label="Description" name="description">
                <TextArea
                  className="w-[550px] rounded-xl"
                  style={{ width: "550px", maxWidth: "550px" }}
                  placeholder="Enter description"
                />
              </Form.Item>

              <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="float-right flex justify-center items-center h-[50px] w-[94px]  rounded-2xl bg-blue-500 px-6 text-base font-medium text-white transition-colors hover:bg-blue-400 disabled:bg-blue-300 disabled:hover:bg-blue-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                >
                  {updateSubmitting ? (
                    <div
                      className="text-white inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                      role="status"
                    ></div>
                  ) : (
                    "Confirm"
                  )}
                </Button>
                <Button
                  onClick={() => setIsUpdateDatabaseModalOpen(false)}
                  className="float-right mr-4 flex justify-center items-center rounded-2xl bg-white h-[50px] w-[94px] border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
                >
                  Cancel
                </Button>
              </Form.Item>
            </Form>
          </Modal>
        </div>
      )}

      <Modal
        title={"Create Database"}
        open={isCreateDatabaseModalOpen}
        closable={false}
        centered
        footer={null}
        width={600}
        destroyOnClose={true}
        wrapClassName="database-modal"
      >
        <Form
          form={createForm}
          name="basic"
          layout="vertical"
          onFinish={onCreate}
          className="pt-4"
          autoComplete="off"
          clearOnDestroy
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[
              { required: true, message: "Please input the database name!" },
            ]}
          >
            <Input
              className="w-[550px] h-[54px] rounded-xl"
              placeholder="Enter Name"
            />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <TextArea
              className="w-[550px] rounded-xl"
              style={{ width: "550px", maxWidth: "550px" }}
              placeholder="Enter description"
            />
          </Form.Item>

          <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              className="float-right flex justify-center items-center h-[50px] w-[94px]  rounded-2xl bg-blue-500 px-6 text-base font-medium text-white transition-colors hover:bg-blue-400 disabled:bg-blue-300 disabled:hover:bg-blue-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            >
              {createSubmitting ? (
                <div
                  className="text-white inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                  role="status"
                ></div>
              ) : (
                "Confirm"
              )}
            </Button>
            <Button
              onClick={() => setIsCreateDatabaseModalOpen(false)}
              className="float-right mr-4 flex justify-center items-center rounded-2xl bg-white h-[50px] w-[94px] border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={isDeleteDatabaseModalOpen}
        closable={false}
        centered
        footer={null}
        width={600}
        destroyOnClose={true}
      >
        <div className="h-[150px]">
          <h1 className="text-xl font-bold">
            Do you want to delete the database?
          </h1>
          <div className="text-base mt-4">
            Deleting the database will permanently remove all associated tags
            and entries.
          </div>

          <button
            className="float-right ml-4 rounded-2xl w-[90px] h-[52px] flex items-center justify-center bg-red-500 text-sm font-medium text-white transition-colors hover:bg-red-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500 active:bg-red-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            onClick={handleDeleteDatabase}
            disabled={deleteSubmitting}
          >
            {deleteSubmitting ? (
              <div
                className="text-white inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              ></div>
            ) : (
              "Delete"
            )}
          </button>
          <button
            className="float-right rounded-2xl bg-white px-6 py-4 border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            onClick={() => {
              setIsDeleteDatabaseModalOpen(false);
            }}
          >
            Cancel
          </button>
        </div>
      </Modal>
    </div>
  );
}
